import React, { Fragment, useEffect, useState } from "react";
import { CardBody, Col, Row, Table } from "reactstrap";
import Select from "react-select";
import { Link } from "react-router-dom";
import {
  Column,
  Table as ReactTable,
  ColumnFiltersState,
  FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import { getPaginationRange } from "services/utils";
import { rowsPerPageOptions } from "./coursehelper";
import { RootState } from "store";

interface CourseContainerProps {
  columns: any[];
  data: any[];

  customPageSize?: number;

  tableClass?: string;
  theadClass?: string;
  trClass?: string;
  thClass?: string;
  divClass?: string;

  SearchPlaceholder?: string;

  pagination?: {
    page: number;
    total_items: number;
    total_pages: number;
    per_page: number;
  };

  onPageChange?: (page: number) => void;

  limit: number;
  setLimit?: React.Dispatch<React.SetStateAction<number>>;

  setPage: (page: number) => void;

  setRowId: React.Dispatch<React.SetStateAction<string | null>>;
  onRowClick?: (row: any) => void;
}

const Filter = ({ column }: { column: Column<any, unknown>; table: ReactTable<any> }) => {
  const columnFilterValue = column.getFilterValue();
  return (
    <input
      type="text"
      value={(columnFilterValue ?? "") as string}
      onChange={(e) => column.setFilterValue(e.target.value)}
      placeholder="Search..."
      className="form-control search"
    />
  );
};

const DebouncedInput = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) => {
  const [value, setValue] = useState(initialValue);

  React.useEffect(() => setValue(initialValue), [initialValue]);
  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);
    return () => clearTimeout(timeout);
  }, [value, onChange, debounce]);

  return <input {...props} value={value} onChange={(e) => setValue(e.target.value)} className="form-control search" />;
};

const CourseContainer = ({
  columns,
  data,
  customPageSize,
  tableClass,
  theadClass,
  trClass,
  thClass,
  divClass,
  pagination,
  onPageChange,
  limit,
  setLimit,
  setPage,
  setRowId,
  onRowClick,
}: CourseContainerProps) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
  };

  const table = useReactTable({
    columns,
    data,
    filterFns: { fuzzy: fuzzyFilter },
    state: { columnFilters, globalFilter },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const { getHeaderGroups, getRowModel, setPageSize } = table;

  React.useEffect(() => {
    if (customPageSize) {
      setPageSize(customPageSize);
    }
  }, [customPageSize, setPageSize]);

  return (
    <Fragment>
      <CardBody className={divClass}>
        <Row className="align-items-center mb-3">
         
        </Row>
        <div className="table-responsive table-card">
          <Table hover className={tableClass}>
            <thead className={theadClass}>
              {getHeaderGroups().map((headerGroup) => (
                <tr className={`${trClass ?? ""} cursor-pointer`} key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={thClass ?? "border-none border-0"}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {header.isPlaceholder ? null : (
                        <>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanFilter() && <Filter column={header.column} table={table} />}
                        </>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {getRowModel().rows.map((row, rowIndex) => (
                <tr
                  className={`${trClass ?? ""} cursor-pointer ${rowIndex % 2 === 1 ? "bg-lightgreen" : ""}`}
                  key={row.id}
                  onClick={() => onRowClick?.(row)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      style={{
                        ...(rowIndex % 2 === 0 && { backgroundColor: "#73C3A91F" }),
                        verticalAlign: "middle",                
                        whiteSpace: "normal",
                      }}
                      key={cell.id}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </CardBody>
      {pagination && (
        <Row className="align-items-center mt-2 g-3 text-center text-sm-start">
          <div className="col-sm">
            <div className="text-muted">
              Showing{" "}
              <span className="fw-semibold ms-1">
                {Math.min(pagination.per_page * pagination.page, pagination.total_items)}
              </span>{" "}
              of <span className="fw-semibold">{pagination.total_items}</span> Results
            </div>
          </div>
          <div className="col-sm-auto">
            <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
              <li className={`page-item ${pagination.page <= 1 ? "disabled" : ""}`}>
                <Link to="#" className="page-link" onClick={() => onPageChange?.(pagination.page - 1)}>
                  Previous
                </Link>
              </li>
              {getPaginationRange(pagination.page, pagination.total_pages).map((p, idx) => (
                <li
                  key={idx}
                  className={`page-item ${p === pagination.page ? "active" : ""} ${p === "..." ? "disabled" : ""}`}
                >
                  {p === "..." ? (
                    <span className="page-link">...</span>
                  ) : (
                    <Link to="#" className="page-link" onClick={() => onPageChange?.(p as number)}>
                      {p}
                    </Link>
                  )}
                </li>
              ))}
              <li className={`page-item ${pagination.page >= pagination.total_pages ? "disabled" : ""}`}>
                <Link to="#" className="page-link" onClick={() => onPageChange?.(pagination.page + 1)}>
                  Next
                </Link>
              </li>
            </ul>
          </div>
          {/* <div className="col-sm-auto">
            <Select
              value={rowsPerPageOptions.find((opt) => opt.value === limit)}
              onChange={(selected: any) => {
                setPage(1);
                setLimit?.(selected?.value || 10);
              }}
              options={rowsPerPageOptions}
              isSearchable={false}
              menuPlacement="top"
              id="page-size-select"
            />
          </div> */}
        </Row>
      )}
    </Fragment>
  );
};

export default CourseContainer;
